import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Input, DatePicker, InputNumber, Select, Button, List, Typography, Flex, Divider, Popconfirm, Space, Tooltip, message, Tag, Dropdown } from "antd";
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined, CopyOutlined, ReloadOutlined, EyeOutlined, EditFilled, InfoCircleOutlined, ExportOutlined, CheckCircleOutlined, FileTextOutlined, DollarOutlined, SendOutlined, MoreOutlined } from "@ant-design/icons";
import { useAppContext } from '../../libs/contextLib';
import { isEmpty, startCase } from 'lodash';
import dayjs from '../../dayjs';
import QuoteDetailsModal from '../../components/QuoteDetailsModal';
import DocumentActionsMenu from '../../components/DocumentActionsMenu';
import config from '../../config';
import { DATE_STRING } from '../../model/busRoute';
import { Copy, Trash } from '../../components/Icons';
import DocumentHistory from '../../components/DocumentHistory';

const { TextArea } = Input;
const { Paragraph } = Typography;

const BookingQuote = ({ mode, charter, setBookingWith, onAction, preview, mail }) => {
  const { operator } = useAppContext();
  const [quoteDetailsVisible, setQuoteDetailsVisible] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  const lineItems = useMemo(() => {
    return charter.quoteLineItems.map(item => ({
      ...item,
      total: charter.calculateLineItemAmount(item),
      margin: item.margin || 0,
      discount: item.discount || 0
    }));
  }, [charter]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  useEffect(() => {
    if (operator) {
      setLogoUrl(`https://${config.s3.BUCKET}.s3.ap-southeast-2.amazonaws.com/public/${operator?.operatorId}/logo.png?${Date.now()}`);
    }
  }, [operator]);

  const addLine = useCallback(
    (lineItem = {
      name: null,
      description: null,
      unitPrice: null,
      quantity: 1,
      discount: null,
      amount: null,
      total: null,
    }) => {
      setBookingWith(
        "quoteLineItems",
        [...(charter.quoteLineItems || []), lineItem]
      );
    },
    [setBookingWith, charter]
  );

  const cloneLine = (index) => {
    const clone = { ...charter.quoteLineItems[index] };
    setBookingWith("quoteLineItems", [...charter.quoteLineItems, clone]);
  };

  const deleteLine = (index) => {
    if (charter.quoteLineItems.length <= 1) {
      return;
    }
    setBookingWith(
      "quoteLineItems",
      charter.quoteLineItems.filter((e, i) => i !== index)
    );
  };

  const updateLine = useCallback((index, key, value) => {
    const isEditingAutoGenerated = charter.quoteLineItems[index].type === 'auto-generated';
    const currentLine = charter.quoteLineItems[index];

    const unitPrice = key === 'unitPrice' ? value : currentLine.unitPrice || 0;
    const quantity = key === 'quantity' ? value : currentLine.quantity || 1;
    let discount = key === 'discount' ? value : currentLine.discount || 0;
    if(isEditingAutoGenerated && key !== 'discount') {
      discount = charter.calculateLineItemDiscount({
        ...currentLine,
        sellingPrice: unitPrice,
        unitPrice,
        quantity
      });
    }
    const total = unitPrice * quantity - discount;

    if (isEditingAutoGenerated) {
      if (key === 'unitPrice') {
        const adjustment = value - currentLine.details.baseUnitPrice;
        charter.updateQuoteAdjustments({ unitPrice: adjustment });
      } else if (key === 'quantity') {
        const adjustment = value - currentLine.details.baseQuantity;
        charter.updateQuoteAdjustments({ quantity: adjustment });
      }
    }

    setBookingWith(
      "quoteLineItems",
      charter.quoteLineItems.map((e, i) => {
        if (i === index) {
          return {
            ...e,
            [key]: value,
            [key === 'unitPrice' ? 'sellingPrice' : 'unitPrice']: unitPrice,
            total: total,
            amount: total,
            discount: discount,
          };
        }
        return e;
      })
    );
  }, [setBookingWith, charter]);

  const subtotal = charter.calculateSubTotal();
  const afterDiscount = charter.calculateTotalAfterDiscount();
  const gst = charter.calculateGST();
  const total = charter.calculateTotal();

  const showQuoteDetails = () => {
    setQuoteDetailsVisible(true);
  };

  const validationsMessages = useMemo(() => {
    return charter.getValidationsMessages();
  }, [charter]);

  return (
    <>
      {/* <Card 
        // title={<DocumentActionsMenu 
        //   mode={mode}
        //   charter={charter} 
        //   onAction={onAction}
        //   currentView="quote"
        //   preview={preview}
        //   mail={mail}
        //   type="quote"
        // />} 
        bordered={false} 
        className="card-main"
      > */}
        <Row gutter={[30, 30]} className="mb-4">
          <Col xs={6} className="d-flex flex-column">
            <label>To (Address)</label>
            {mode.edit ? (
              <Input
                placeholder="Enter Address Line 1"
                status={!charter.address1?.length && charter?.customer?.addressLine1?.length ? 'error' : ''}
                value={charter.address1}
                onChange={({ target: { value } }) => setBookingWith('address1', value)}
              />
            ) : (
              <strong>{startCase(charter.address1)}</strong>
            )}
            {mode.edit ? (
              <Input
                placeholder="Enter Address Line 2"
                value={charter.address2}
                onChange={({ target: { value } }) => setBookingWith('address2', value)}
              />
            ) : (
              <strong>{startCase(charter.address2)}</strong>
            )}
            {mode.edit ? (
              <Input
                placeholder="Enter Contact Number"
                // status={!charter.addressContact?.length ? 'error' : ''}
                value={charter.addressContact}
                onChange={({ target: { value } }) => setBookingWith('addressContact', value)}
              />
            ) : (
              <strong>{charter.addressContact}</strong>
            )}
          </Col>
          <Col xs={8} className="d-flex flex-column">
            <label>Quote Description</label>
            {/* <strong>Picton HS Swimming Carnival</strong> */}
            {mode.edit ? (
              <TextArea
                autoSize={{ minRows: 4 }}
                placeholder="Enter quote description"
                value={charter.quoteDescription}
                onChange={({ target: { value } }) => setBookingWith('quoteDescription', value)}
              />
            ) : (<small>{(charter.quoteDescription || '').split('\n').map(p =>
              <Paragraph>{p}</Paragraph>)}</small>)}
          </Col>
          <Col xs={5} className="d-flex flex-column">
            <label>Quote Date</label>
            {mode.edit ? (
              <DatePicker
                placeholder="Quote Date"
                {...(!isEmpty(charter.quoteDate) ? { value: charter.quoteDate } : { value: dayjs() })}
                status={false}
                format={{
                  format: DATE_STRING,
                  type: 'mask',
                }}
                onChange={(value) => {
                  // If date is cleared, default to today's date
                  const dateValue = value || dayjs();
                  setBookingWith('quoteDate', dateValue);
                  if (!charter.paymentDue) {
                    setBookingWith('paymentDue', dateValue.add(2, 'w'));
                  }
                }}
              />
            ) : (
              <strong>
                {charter.quoteDate?.format ? charter.quoteDate.format(DATE_STRING) : 'TBC'}
              </strong>
            )}
          </Col>
          <Col xs={5} className="d-flex align-items-center justify-content-end">
            {logoUrl &&
              <img
                width={150}
                src={logoUrl}
                onError={(e) => {
                  e.nativeEvent.target.style.width = 0;
                  e.nativeEvent.target.style.height = 0;
                }}
              />}
          </Col>
          {/* <Col xs={6} className="d-flex flex-column">
          <label>Payment Due</label>
          {mode.edit ? (
            <DatePicker
              placeholder="Payment Due"
              {...(!isEmpty(charter.paymentDue) ? { value: charter.paymentDue } : {})}
              status={!charter.paymentDue ? 'error' : ''}
              format={{
                format: DATE_STRING,
                type: 'mask',
              }}
              onChange={(value) => {
                setBookingWith('paymentDue', value);
              }}
            />
          ) : (
            <strong>
              {charter.paymentDue?.format ? charter.paymentDue.format(DATE_STRING) : 'TBC'}
            </strong>
          )}
        </Col> */}

          {/* <Col xs={6} className="d-flex flex-column">
          <label>Ref</label>
          {mode.edit ? (
            <Input
              placeholder="Enter Ref"
              status={!charter.ref?.length ? 'error' : ''}
              value={charter.ref}
              onChange={({ target: { value } }) => setBookingWith('ref', value)}
            />
          ) : (
            <strong>{startCase(charter.ref)}</strong>
          )}
        </Col>
        <Col xs={6} className="d-flex flex-column">
          <label>Invoice No</label>
          {mode.edit ? (
            <Input
              placeholder="Enter Invoice Number"
              status={!charter.invoiceNumber?.length ? 'error' : ''}
              value={charter.invoiceNumber}
              onChange={({ target: { value } }) => setBookingWith('invoiceNumber', value)}
            />
          ) : (
            <strong>{startCase(charter.invoiceNumber)}</strong>
          )}
        </Col> */}
        </Row>
        {mode.edit && (
          <div className="d-flex justify-content-end mb-3">
            {/* <Button type="primary" className="icon-button btn-sm card-btn" onClick={() => generateQuote()} icon={<PlusOutlined />}>
            Auto-generate Quote
          </Button> */}
            {charter.quoteLineItems?.some(line => line.type === 'auto-generated') && (
              <Button
                type="default"
                className="icon-button btn-sm"
                style={{ marginRight: 10 }}
                onClick={showQuoteDetails}
              >
                <EyeOutlined /> Quote Details
              </Button>
            )}
            <Button type="primary" className="icon-button btn-sm" onClick={() => addLine()} icon={<PlusOutlined />}>
              Add Line Item
            </Button>
          </div>
        )}
        {!mode.edit && charter.quoteLineItems?.some(line => line.type === 'auto-generated') && (
          <div className="d-flex justify-content-end mb-3">
            <Button
              type="default"
              className="icon-button btn-sm"
              onClick={showQuoteDetails}
            >
              <EyeOutlined /> Quote Details
            </Button>
          </div>
        )}
        <hr className="row-separator full-width" />
        <Row className="card-row-header align-items-center">
          <Col xs={6}>
            <span>Name</span>
          </Col>
          <Col xs={8} className="text-left">
            <span>Description</span>
          </Col>
          <Col xs={2}>
            <span>Price</span>
          </Col>
          <Col xs={2}>
            <span>Qty</span>
          </Col>
          <Col xs={2}>
            <span>Discount</span>
          </Col>
          <Col xs={2} className="text-right">
            <span>Amount</span>
          </Col>
          {mode.edit && (
            <Col xs={2}>
              <span>Actions</span>
            </Col>
          )}
        </Row>
        {lineItems?.map((line, index) => (
          <Row key={`qline-${index}`} className={`card-row-content border-full align-items-center ${line.type === 'auto-generated' ? 'auto-generated' : ''}`}>
            <Col xs={6}>
              {mode.edit ? (
                <Input
                  size="small"
                  placeholder="Name"
                  style={{ maxWidth: '98%' }}
                  status={!line.name?.length ? "error" : ""}
                  value={line.name}
                  onChange={({ target: { value } }) => updateLine(index, "name", value)}
                />
              ) : (
                <strong>{startCase(line.name)}</strong>
              )}
            </Col>
            <Col xs={8} className="text-left" style={{ lineHeight: '16px' }}>
              {mode.edit ? (
                <Input
                  size="small"
                  placeholder="Description"
                  style={{ maxWidth: '99%' }}
                  value={line.description}
                  onChange={({ target: { value } }) => updateLine(index, "description", value)}
                />
              ) : (
                <span>{line.description}</span>
              )}
            </Col>
            <Col xs={2}>
              {mode.edit ? (
                <InputNumber
                  size="small"
                  placeholder="Price"
                  style={{ maxWidth: '98%' }}
                  min={0}
                  formatter={value => formatCurrency(value)}
                  parser={value => value.replace(/[^\d.-]/g, '')}
                  value={line.unitPrice}
                  onChange={(value) => updateLine(index, "unitPrice", value)}
                />
              ) : (
                <span>{formatCurrency(line.unitPrice)}</span>
              )}
            </Col>
            <Col xs={2}>
              {mode.edit ? (
                <InputNumber
                  size="small"
                  placeholder="Qty"
                  style={{ maxWidth: '98%' }}
                  min={0}
                  value={line.quantity}
                  onChange={(value) => updateLine(index, "quantity", value)}
                />
              ) : (
                <span>{line.quantity}</span>
              )}
            </Col>
            <Col xs={2}>
              {mode.edit ? (
                <InputNumber
                  size="small"
                  placeholder="Discount"
                  style={{ maxWidth: '98%' }}
                  min={0}
                  value={line.discount || 0}
                  formatter={value => formatCurrency(value)}
                  parser={value => value.replace(/[^\d.-]/g, '')}
                  onChange={(value) => updateLine(index, "discount", value)}
                />
              ) : (
                <span>{formatCurrency(line.discount)}</span>
              )}
            </Col>
            <Col xs={2} className="text-right">
              {formatCurrency(line.total)}
            </Col>
            <Col xs={mode.edit ? 2 : 1} className="row-col-actions hide-print">
              {mode.edit &&
                <>
                  <Button
                    size="small"
                    className="btn-icon-control"
                    onClick={() => cloneLine(index)}
                    icon={<Copy style={{ width: 14 }} />}
                  />
                  <Button
                    size="small"
                    className="btn-icon-control btn-delete"
                    onClick={() => deleteLine(index)}
                    icon={<Trash style={{ width: 14 }} />}
                  />
                </>}
              {/* Show warning if number of vehicles is 0 */}
              {line.type === 'auto-generated' && validationsMessages.length > 0 && (
                <Tooltip title={<div>
                  {validationsMessages.map(v => (
                    <div key={v.field}>
                      <p>{v.message}</p>
                    </div>
                  ))}
                </div>}>
                  <InfoCircleOutlined style={{ width: 14, color: 'red' }} />
                </Tooltip>
              )}
            </Col>
          </Row>
        ))}
        <div className="invoice-totals" style={{marginTop: 30}}>
          <Row className="card-row-content border-full align-items-center no-border">
            <Col className="label-col">
              <strong>Line Item Total</strong>
            </Col>
            <Col className="value-col text-right">
              <strong style={{ fontSize: "18px" }}>
                {formatCurrency(subtotal)}
              </strong>
            </Col>
          </Row>
          <Row className="card-row-content border-full align-items-center no-border">
            <Col className="label-col">
              <strong>SUBTOTAL</strong>
            </Col>
            <Col className="value-col text-right">
              <strong style={{ fontSize: "18px" }}>
                {formatCurrency(afterDiscount)}
              </strong>
            </Col>
          </Row>
          <Row className="card-row-content border-full align-items-center no-border">
            <Col className="label-col">
              <strong>GST (10%)</strong>
            </Col>
            <Col className="value-col text-right">
              <strong style={{ fontSize: "15px" }}>
                {formatCurrency(gst)}
              </strong>
            </Col>
          </Row>
          <Row className="card-row-content border-full align-items-center">
            <Col className="label-col">
              <strong style={{ fontSize: "18px" }}>TOTAL</strong>
            </Col>
            <Col className="value-col text-right">
              <strong style={{ fontSize: "18px" }}>
                {formatCurrency(total)}
              </strong>
            </Col>
          </Row>
        </div>

        <QuoteDetailsModal
          visible={quoteDetailsVisible}
          onClose={() => setQuoteDetailsVisible(false)}
          charter={charter}
          editMode={mode.edit}
          setBookingWith={setBookingWith}
        />

        {/* <DocumentHistory charter={charter} /> */}
      {/* </Card> */}
    </>
  );
};

export default BookingQuote; 